import type { ReactNode } from 'react';
import type { EmailAddress, MessageSummary } from '../../api/types';
import { attachmentsOf, displayName, isFlagged, isUnread } from '../../api/types';
import { listDate } from '../../format';
import { Button, Input } from '../../ui';

export type BulkAction = 'archive' | 'delete' | 'spam' | 'read' | 'unread';

interface Props {
	title: string;
	messages: MessageSummary[];
	openUid: number | null;
	selected: Set<number>;
	loading: boolean;
	error: string;
	total: number;
	search: string;
	searchPlaceholder: string;
	emptyText: string;
	/** Show who a message went to instead of who sent it -- for Sent and Drafts. */
	showRecipients?: boolean;
	/** Which bulk actions make sense in this view. */
	canArchive: boolean;
	canSpam: boolean;
	/** Notices shown above the list (notification setup, send warnings). */
	banner?: ReactNode;
	onSearch: (value: string) => void;
	onOpen: (message: MessageSummary) => void;
	onToggleSelect: (uid: number) => void;
	onSelectAll: () => void;
	onClearSelection: () => void;
	onBulk: (action: BulkAction) => void;
	onStar: (message: MessageSummary, starred: boolean) => void;
	onLoadMore: () => void;
	onMenu: () => void;
	onRefresh: () => void;
}

function recipients(to: EmailAddress[] | undefined): string {
	const names = (to ?? []).map(address => displayName([address])).filter(Boolean);
	return names.length ? `To: ${names.join(', ')}` : 'To: (no recipients)';
}

/** First letter of the name, for the avatar. */
function initial(name: string): string {
	const letter = name.replace(/^[^\p{L}\p{N}]+/u, '').charAt(0);
	return letter ? letter.toUpperCase() : '?';
}

/**
 * Monochrome system, so avatars differ by shade rather than hue: a stable grey
 * per sender that makes a busy list easier to scan without adding a colour.
 */
function shade(key: string): string {
	let hash = 0;
	for (let i = 0; i < key.length; ++i) {
		hash = (hash * 31 + key.charCodeAt(i)) | 0;
	}
	return `hsl(0 0% ${22 + (Math.abs(hash) % 5) * 6}%)`;
}

export function MessageList(props: Props) {
	const {
		title,
		messages,
		openUid,
		selected,
		loading,
		error,
		total,
		search,
		searchPlaceholder,
		emptyText,
		showRecipients,
		canArchive,
		canSpam,
		banner,
		onSearch,
		onOpen,
		onToggleSelect,
		onSelectAll,
		onClearSelection,
		onBulk,
		onStar,
		onLoadMore,
		onMenu,
		onRefresh
	} = props;

	const hasMore = messages.length < total;
	const selecting = selected.size > 0;
	const allSelected = selecting && selected.size === messages.length;
	const anyUnread = messages.some(message => selected.has(message.uid) && isUnread(message));

	return (
		<section className="pane list" aria-label="Messages">
			{selecting ? (
				// Replaces the header while selecting, as Gmail does, so the actions
				// sit exactly where the eye already is. Every action is written out.
				<header className="pane__header list__bulk">
					<Button variant="ghost" size="sm" onClick={onClearSelection} aria-label="Cancel selection">
						✕
					</Button>
					<span className="list__bulk-count">{selected.size} selected</span>
					<div className="list__bulk-actions">
						{canArchive && (
							<Button variant="ghost" size="sm" onClick={() => onBulk('archive')}>Archive</Button>
						)}
						<Button variant="ghost" size="sm" onClick={() => onBulk('delete')}>Delete</Button>
						{canSpam && (
							<Button variant="ghost" size="sm" onClick={() => onBulk('spam')}>Spam</Button>
						)}
						<Button variant="ghost" size="sm" onClick={() => onBulk(anyUnread ? 'read' : 'unread')}>
							{anyUnread ? 'Mark read' : 'Mark unread'}
						</Button>
						<Button variant="ghost" size="sm" onClick={allSelected ? onClearSelection : onSelectAll}>
							{allSelected ? 'Select none' : 'Select all'}
						</Button>
					</div>
				</header>
			) : (
				<header className="pane__header">
					<Button
						variant="ghost"
						size="sm"
						className="pane__back"
						onClick={onMenu}
						aria-label="Open menu"
					>
						☰
					</Button>
					<h2 className="pane__title">{title}</h2>
					<Button
						variant="ghost"
						size="sm"
						style={{ marginLeft: 'auto' }}
						onClick={onRefresh}
						disabled={loading}
						aria-label="Check for new mail"
						title="Check for new mail"
					>
						↻
					</Button>
				</header>
			)}

			{banner}

			<div className="list__search">
				<Input
					type="search"
					value={search}
					onChange={event => onSearch(event.target.value)}
					placeholder={searchPlaceholder}
					aria-label={searchPlaceholder}
					size="sm"
					startContent="⌕"
					data-shortcut="search"
				/>
			</div>

			{error && <p className="list__empty ks-error">{error}</p>}

			{!error && !messages.length && !loading && (
				<p className="list__empty">{search ? 'Nothing matched that search.' : emptyText}</p>
			)}

			{messages.map(message => {
				const unread = isUnread(message);
				const starred = isFlagged(message);
				const checked = selected.has(message.uid);
				const who = showRecipients ? recipients(message.to) : displayName(message.from);
				const hasFiles = attachmentsOf(message).length > 0;

				return (
					<div
						key={`${message.folder}:${message.uid}`}
						className={[
							'message-row',
							unread ? 'message-row--unread' : '',
							checked ? 'message-row--checked' : ''
						].join(' ')}
						aria-current={message.uid === openUid}
					>
						<button
							className="message-row__avatar"
							style={checked ? undefined : { background: shade(who) }}
							onClick={() => onToggleSelect(message.uid)}
							aria-pressed={checked}
							aria-label={checked ? `Deselect message from ${who}` : `Select message from ${who}`}
							title={checked ? 'Deselect' : 'Select'}
						>
							{checked ? '✓' : initial(who.replace(/^To: /, ''))}
						</button>

						<button
							className="message-row__main"
							// While selecting, tapping a row adds it to the selection
							// rather than opening it -- same as Gmail on a phone.
							onClick={() => (selecting ? onToggleSelect(message.uid) : onOpen(message))}
						>
							<span className="message-row__top">
								{unread && <span className="message-row__dot" aria-label="Unread" />}
								<span className="message-row__from">{who}</span>
								<span className="message-row__date">{listDate(message.dateTimestamp)}</span>
							</span>
							<span className="message-row__subject">
								{message.subject || '(no subject)'}
								{hasFiles && (
									<span className="message-row__clip" aria-label="Has attachments" title="Has attachments">
										📎
									</span>
								)}
							</span>
							{message.preview && <span className="message-row__preview">{message.preview}</span>}
						</button>

						<button
							className={`message-row__star${starred ? ' is-on' : ''}`}
							onClick={() => onStar(message, !starred)}
							aria-pressed={starred}
							aria-label={starred ? 'Unstar' : 'Star'}
							title={starred ? 'Unstar' : 'Star'}
						>
							{starred ? '★' : '☆'}
						</button>
					</div>
				);
			})}

			{loading && <p className="list__empty">Loading…</p>}

			{hasMore && !loading && (
				<div className="list__more">
					<Button variant="outline" block onClick={onLoadMore}>Load more</Button>
				</div>
			)}
		</section>
	);
}
